import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Company {
  id: string;
  name: string;
  subscription_tier: "free" | "pro" | "scale" | null;
  intended_subscription_tier: "pro" | "scale" | "fleet" | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyMember {
  id: string;
  company_id: string;
  user_id: string;
  role: "owner" | "member";
  created_at: string;
  profiles?: {
    full_name: string | null;
    email: string | null;
  };
}

export interface CompanyInvitation {
  id: string;
  company_id: string;
  email: string;
  invited_by: string;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export function useCompany() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track userId via auth state changes so query key is user-aware
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Seed with the current session (sync-safe: getSession reads from memory)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Get current user's company
  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ["company", userId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: membership, error: memberError } = await supabase
        .from("company_members")
        .select("company_id, role")
        .eq("user_id", user.id)
        .maybeSingle();

      if (memberError || !membership) return null;

      const { data: company, error: companyError } = await supabase
        .from("companies")
        .select("*")
        .eq("id", membership.company_id)
        .single();

      if (companyError) return null;

      return { ...company, userRole: membership.role as "owner" | "member" };
    },
  });

  // Get company members
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ["company-members", company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_members")
        .select("*")
        .eq("company_id", company!.id);

      if (error) throw error;

      // Fetch profile data for each member
      const membersWithProfiles = await Promise.all(
        data.map(async (member) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("full_name, email")
            .eq("user_id", member.user_id)
            .single();
          
          return {
            ...member,
            profiles: profile || { full_name: null, email: null },
          } as CompanyMember;
        })
      );

      return membersWithProfiles;
    },
  });

  // Get pending invitations
  const { data: invitations = [], isLoading: invitationsLoading } = useQuery({
    queryKey: ["company-invitations", company?.id],
    enabled: !!company?.id && company?.userRole === "owner",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_invitations")
        .select("*")
        .eq("company_id", company!.id)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString());

      if (error) throw error;
      return data as CompanyInvitation[];
    },
  });

  // Send invitation mutation
  const sendInvitation = useMutation({
    mutationFn: async (email: string) => {
      if (!company) throw new Error("No company found");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Check if user is already a member
      const { data: existingMember } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("email", email)
        .single();

      if (existingMember) {
        const { data: isMember } = await supabase
          .from("company_members")
          .select("id")
          .eq("company_id", company.id)
          .eq("user_id", existingMember.user_id)
          .single();

        if (isMember) {
          throw new Error("This user is already a member of your company");
        }
      }

      // Check for existing pending invitation
      const { data: existingInvite } = await supabase
        .from("company_invitations")
        .select("id")
        .eq("company_id", company.id)
        .eq("email", email)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .single();

      if (existingInvite) {
        throw new Error("An invitation has already been sent to this email");
      }

      // Create invitation
      const { data: invitation, error } = await supabase
        .from("company_invitations")
        .insert({
          company_id: company.id,
          email,
          invited_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Call edge function to send email
      const inviteUrl = `${window.location.origin}/auth?invite=${invitation.token}`;
      
      const { error: emailError } = await supabase.functions.invoke("send-invitation", {
        body: {
          email,
          companyName: company.name,
          inviteUrl,
        },
      });

      if (emailError) {
        // Delete the invitation if email fails
        await supabase
          .from("company_invitations")
          .delete()
          .eq("id", invitation.id);
        throw new Error("Failed to send invitation email. Please try again.");
      }

      return invitation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-invitations"] });
      toast({ title: "Invitation sent!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error sending invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Cancel invitation
  const cancelInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("company_invitations")
        .delete()
        .eq("id", invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-invitations"] });
      toast({ title: "Invitation cancelled" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove member
  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from("company_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-members"] });
      toast({ title: "Team member removed" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update company name
  const updateCompany = useMutation({
    mutationFn: async (name: string) => {
      if (!company) throw new Error("No company found");

      const { error } = await supabase
        .from("companies")
        .update({ name })
        .eq("id", company.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      toast({ title: "Company updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create company
  const createCompany = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create the company
      const { data: newCompany, error: companyError } = await supabase
        .from("companies")
        .insert({ name })
        .select()
        .single();

      if (companyError) throw companyError;

      // Add user as owner
      const { error: memberError } = await supabase
        .from("company_members")
        .insert({
          company_id: newCompany.id,
          user_id: user.id,
          role: "owner",
        });

      if (memberError) {
        // Rollback company creation
        await supabase.from("companies").delete().eq("id", newCompany.id);
        throw memberError;
      }

      return newCompany;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["company-members"] });
      toast({ title: "Company created!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating company",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    company,
    members,
    invitations,
    isLoading: companyLoading || membersLoading || invitationsLoading,
    isCompanyLoading: companyLoading,
    isOwner: company?.userRole === "owner",
    sendInvitation,
    cancelInvitation,
    removeMember,
    updateCompany,
    createCompany,
  };
}
